import type { ReactNode } from 'react';
import { errorMessage, isRetryable } from '@/mocks/errors';

/**
 * Loading, empty, error and restricted states.
 *
 * 07_STATE_MACHINE_AND_EDGE_CASE_CATALOG.md forbids generic blank screens.
 * Skeletons match the final geometry so nothing reflows when data arrives.
 *
 * Every state icon is a real stroked SVG mark, never a text glyph standing in
 * for one — a text character at icon size renders inconsistently across
 * platforms and reads as an unfinished placeholder.
 */

const iconProps = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

function InboxIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3.5 12h4.2l1.6 3h5.4l1.6-3h4.2" />
      <path d="M6 5.5h12L20.5 12v6.5a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V12L6 5.5Z" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 9v4" />
      <path d="M12 16.5h.01" />
      <path d="M10.6 3.9 2.9 18a1.5 1.5 0 0 0 1.3 2.3h15.6a1.5 1.5 0 0 0 1.3-2.3L13.4 3.9a1.6 1.6 0 0 0-2.8 0Z" />
    </svg>
  );
}

function LockedIcon() {
  return (
    <svg {...iconProps}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
    </svg>
  );
}

export function Skeleton({
  width = '100%',
  height = 16,
  radius = 6,
}: {
  width?: string | number;
  height?: string | number;
  radius?: number;
}) {
  return (
    <span
      className="tm-skeleton"
      aria-hidden="true"
      style={{
        display: 'block',
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        borderRadius: radius,
      }}
    />
  );
}

/** Row skeleton whose geometry matches ListRow exactly (56px, avatar + 2 lines). */
export function RowSkeleton({ count = 4 }: { count?: number }) {
  return (
    <ul className="tm-rows" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <li key={i}>
          <div className="tm-row" style={{ cursor: 'default' }}>
            <Skeleton width={38} height={38} radius={19} />
            <div className="tm-row__body">
              <Skeleton width="58%" height={14} />
              <div style={{ height: 6 }} />
              <Skeleton width="36%" height={12} />
            </div>
            <Skeleton width={64} height={14} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function CardSkeleton({ height = 132 }: { height?: number }) {
  return <Skeleton height={height} radius={14} />;
}

/**
 * Every async region announces its own loading state so a screen reader user
 * is told the page is working, not left in silence.
 */
export function LoadingRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="tm-visually-hidden">{label}</span>
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  /** Defaults to a real inbox mark rather than a bare circle glyph. */
  icon?: ReactNode;
}) {
  return (
    <div className="tm-state">
      <span className="tm-state__icon tm-state__icon--empty">{icon ?? <InboxIcon />}</span>
      <h2 className="tm-state__title">{title}</h2>
      <p className="tm-state__body">{body}</p>
      {action}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  title = 'We could not load this',
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const retryable = isRetryable(error);
  return (
    <div className="tm-state" role="alert">
      <span className="tm-state__icon tm-state__icon--error">
        <AlertIcon />
      </span>
      <h2 className="tm-state__title">{title}</h2>
      <p className="tm-state__body">{errorMessage(error)}</p>
      {retryable && onRetry ? (
        <button type="button" className="tm-btn tm-btn--secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/**
 * Shown when a capability is denied with `explained` visibility, or when a deep
 * link reaches a hidden route. The wording never confirms whether the feature
 * exists for other tenants.
 */
export function RestrictedState({
  message,
  title = 'Not available',
  action,
}: {
  message: string;
  title?: string;
  action?: ReactNode;
}) {
  return (
    <div className="tm-state" role="status">
      <span className="tm-state__icon tm-state__icon--locked">
        <LockedIcon />
      </span>
      <h2 className="tm-state__title">{title}</h2>
      <p className="tm-state__body">{message}</p>
      {action}
    </div>
  );
}

const NOTICE_ICON: Record<'info' | 'warning' | 'danger' | 'success', ReactNode> = {
  info: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.6 4.4 3.4 17a1.4 1.4 0 0 0 1.2 2.1h14.8a1.4 1.4 0 0 0 1.2-2.1L13.4 4.4a1.5 1.5 0 0 0-2.8 0Z" />
      <path d="M12 10v3.2" />
      <path d="M12 16h.01" />
    </svg>
  ),
  danger: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16h.01" />
    </svg>
  ),
  success: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12.5 9 17l11-11" />
    </svg>
  ),
};

export function Notice({
  tone,
  title,
  children,
  action,
}: {
  tone: 'info' | 'warning' | 'danger' | 'success';
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`tm-notice tm-notice--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <span className="tm-notice__icon" aria-hidden="true">
        {NOTICE_ICON[tone]}
      </span>
      <div className="tm-notice__body">
        {title ? <strong className="tm-notice__title">{title}</strong> : null}
        {children}
        {action ? <div style={{ marginBlockStart: 8 }}>{action}</div> : null}
      </div>
    </div>
  );
}
